import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { __ } from '@wordpress/i18n';

// tools
import handleActionError from '../../helpers/handleActionError';
import handleInfoAndDisplay from '../../helpers/handleInfoAndDisplay';
import useDebounce from '../../hooks/useDebounce';
import useProjectRole from '../../hooks/useProjectRole';

// store
import { storeSet, useStoreValue } from '../../store';
import { queryKeys } from '../../lib/query-client';

// helpers
import { EMPTY_SELECTION, SELECTION_KINDS } from '../../helpers/selection';

// service
import CommunicationsService from '../../service/communications-service';
import ProjectService from '../../service/project-service';

// comm center
import {
    buildCueIdIndex,
    buildCueIndex,
    buildUserIndex,
    detectCueRefs,
    matchesMessageScope,
    matchesScope,
    resolveCueIds,
    toActivity,
    toComment,
    toDraft,
    toLinkedActivity,
} from './adapters';

const CommCenterContext = createContext(null);

export const useCommCenter = () => {
    const ctx = useContext(CommCenterContext);

    if (!ctx) {
        throw new Error('useCommCenter must be used inside <CommCenterProvider/>');
    }

    return ctx;
}

/** Semantic search only kicks in once the term carries some meaning. */
const SEARCH_MIN_LENGTH = 3;

/**
 * The Comm Center writes are role gated on the backend, and the errors are
 * reported by the callers below, so the global mutation toast is switched off
 * here to keep one failure to one message.
 */
const SILENT_ERROR = { onError: () => {} };

/**
 * Event types the backend emits into `/me/activities` only — the project task
 * list leaves them out, so the project feed picks them up from there.
 *
 * `comment` belongs here too: without it a new comment only ever showed up in
 * the Communications tab and the "Commenti" filter of the Activities tab could
 * never match anything. `deadline` is the daily job that warns 48h and 24h
 * before a cue is due, and once it is late.
 */
const INFORMATIVE_TYPES = ['comment', 'team_join', 'cue_updated', 'completed_cue', 'deadline'];

/** Selection kind -> the `entityType` the API files an activity under. */
const SCOPE_ENTITY_TYPES = {
    [SELECTION_KINDS.CUE]: 'cue_sheet_item',
    [SELECTION_KINDS.VIDEO]: 'video',
    [SELECTION_KINDS.REC]: 'rec_session',
};

/** How long a card stays highlighted after a jump between the two tabs. */
const HIGHLIGHT_MS = 2600;

/**
 * The feed of one project, newest first: its tasks and its informative events.
 *
 * `includeEvents` is what brings the events of the *project* — everybody's, not
 * only mine. The personal feed is still read on top of it because the flag
 * covers `team_join`, `cue_updated`, `completed_cue` and `deadline`, and the
 * `comment` events the "Commenti" chip filters on are not among them; the two
 * lists overlap, so they are merged on id. It stays a nice-to-have: if the
 * personal feed fails, the project one still loads.
 *
 * `scope` narrows both lists server side — it is what makes a selection filter
 * the whole history instead of only the page that happens to be loaded.
 */
const listProjectFeedAsync = async (projectId, scope = {}) => {
    const [tasks, mine] = await Promise.all([
        CommunicationsService.listActivitiesAsync(projectId, { ...scope, includeEvents: true }),
        CommunicationsService.listMyActivitiesAsync({ projectId, ...scope }).catch(() => null),
    ]);

    // Read / archived are per user flags and `/me/activities` is where they
    // live. The project list is meant to echo them but answers `read: false`
    // for a card already marked read (UAT, 24/09/2026), so where both lists
    // carry an activity the personal copy decides the flags.
    const flags = new Map((mine?.items || []).map(item => [item.id, item]));
    const taskItems = (tasks?.items || []).map(item => {
        const own = flags.get(item.id);

        return own ? { ...item, read: own.read, archived: own.archived } : item;
    });
    const seen = new Set(taskItems.map(item => item.id));
    // `projectId` above is server side, but a backend that ignores it would
    // otherwise leak another project's events into this feed.
    const events = (mine?.items || []).filter(item => String(item.projectId) === String(projectId)
        && INFORMATIVE_TYPES.includes(item.type)
        && !seen.has(item.id));

    const items = [...taskItems, ...events]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return { ...tasks, items };
}

/**
 * Holds the Comm Center state for one sidebar instance: the activity feed, the
 * communications feed, the active tab and the filters.
 *
 * Two scopes:
 *   'project' — one project: activities + communications + (optionally) brief
 *   'me'      — the cross project feed of the Projects page, activities only
 *
 * What each member may do comes from useProjectRole, which mirrors the backend
 * capability table; `can` is handed to every consumer so a control that would
 * answer 403 is never rendered. Outside a project (scope 'me') no role can be
 * resolved, so the feed there is read only apart from read / archive, which are
 * personal flags open to everybody.
 *
 * @param {object}   props
 * @param {string}   props.cueTarget   what clicking a cue tag means here:
 *                                     'studio' (navigate) | 'cuesheet' | 'timeline'
 * @param {string}   props.projectId   current project, when the sidebar is scoped to one
 * @param {string}   props.scope       'project' | 'me'
 * @param {boolean}  props.hasBrief    render the Brief tab
 * @param {object}   props.briefFallback  stand-in project for the Brief tab when
 *                                        the store holds no matching project
 */
export const CommCenterProvider = ({
    children,
    cueTarget = 'studio',
    projectId = null,
    scope = projectId ? 'project' : 'me',
    hasBrief = false,
    briefFallback = null,
}) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const projects = useStoreValue('projects');
    const userData = useStoreValue('userData');
    const currentUserId = userData?.id || null;

    const isProjectScope = scope === 'project' && Boolean(projectId);

    const { role, team, can, isLoading: roleLoading, refreshRole } = useProjectRole(
        isProjectScope ? projectId : null
    );

    const [activeTab, setActiveTab] = useState('activities');
    const [activityFilter, setActivityFilter] = useState('all');
    const [showArchived, setShowArchived] = useState(false);
    const [commentSearch, setCommentSearch] = useState('');
    const [commentFilter, setCommentFilter] = useState('all');
    // Drafts returned by "Genera attività" before the activity list has caught up.
    const [localDrafts, setLocalDrafts] = useState({});   // messageId -> draft
    // Unsaved edits laid over a draft, and the drafts closed away for this session.
    const [draftEdits, setDraftEdits] = useState({});     // messageId -> patch
    const [dismissedDrafts, setDismissedDrafts] = useState([]);
    // Card the sidebar just jumped to from the other tab, so it can be found.
    const [highlight, setHighlight] = useState({ commentId: null, activityId: null });
    const highlightTimer = useRef(null);

    // --------------------------------------------------------------------- queries

    // The project pages already hold the cue sheet in the store; the sidebar only
    // fetches it when it is mounted somewhere that does not (and never under
    // queryKeys.cueSheet, which caches a different shape).
    const storedCueSheet = useStoreValue('cueSheet');
    const hasStoredCueSheet = Boolean(storedCueSheet?.length);

    const cueSheetQuery = useQuery({
        queryKey: [...queryKeys.project(projectId), 'cue-sheet', 'comm-center'],
        queryFn: () => ProjectService.getProjectCueSheetAsync(projectId),
        enabled: isProjectScope && !hasStoredCueSheet,
    });

    const cueSheetItems = hasStoredCueSheet ? storedCueSheet : cueSheetQuery.data;

    // ------------------------------------------------------------------- scope

    /**
     * What the user has selected elsewhere on the page — cue sheet rows, tracks
     * on the timeline, items in the left panel of My Studio. While something is
     * selected the feed only shows what belongs to it.
     *
     * An activity now carries the entity it is about (`cueSheetItemId`, or
     * `entityType` + `entityId` for a video / rec session), and the two activity
     * lists filter on it server side, so the scope is applied to the whole
     * history rather than to the page that happens to be loaded.
     *
     * A message carries the cues it refers to as `cueSheetItemIds`, and the
     * communications list filters on them server side too. A message never
     * refers to a video or a rec session, so only a cue selection scopes the
     * Communications tab (API_GAPS_FOR_BE.md §6b).
     */
    const selection = useStoreValue('selection');

    const selectedCueItems = useMemo(() => {
        if (selection?.kind !== SELECTION_KINDS.CUE) return [];

        return (cueSheetItems || []).filter(item => (selection.ids || []).includes(item.id));
    }, [selection, cueSheetItems]);

    const activityScope = useMemo(() => {
        const ids = selection?.ids || [];
        const entityType = SCOPE_ENTITY_TYPES[selection?.kind];

        if (!entityType || ids.length === 0) return null;

        const isCue = selection.kind === SELECTION_KINDS.CUE;

        return {
            kind: selection.kind,
            entityType,
            ids,
            count: ids.length,
            // Several cues can share a label, and repeating it reads like a bug.
            labels: [...new Set(selectedCueItems.map(item => item.label || item.title).filter(Boolean))],
            codes: selectedCueItems
                .map(item => String(item.label || item.title || '').toLowerCase())
                .filter(Boolean),
            // The Comunicazioni tab has nothing to match a video or a rec
            // session against, and says so instead of looking like an empty feed.
            supportsComments: isCue,
        };
    }, [selection, selectedCueItems]);

    /**
     * The scope as query params. `cueSheetItemId` and `entityId` repeat, so the
     * whole selection travels and the filter holds over the entire history
     * rather than over the page that happens to be loaded.
     */
    const serverScope = useMemo(() => {
        if (!activityScope) return {};

        return activityScope.entityType === 'cue_sheet_item'
            ? { cueSheetItemId: activityScope.ids }
            : { entityType: activityScope.entityType, entityId: activityScope.ids };
    }, [activityScope]);

    const clearActivityScope = useCallback(() => storeSet('selection', EMPTY_SELECTION), []);

    const inScope = useCallback((activity) => {
        if (!activityScope) return true;

        return matchesScope(activityScope, activity);
    }, [activityScope]);

    /**
     * The same scope over the Comunicazioni tab. The list already comes back
     * scoped; this still runs over the semantic search hits, which are not.
     */
    const commentInScope = useCallback((comment) => {
        if (!activityScope) return true;
        if (!activityScope.supportsComments) return false;

        return matchesMessageScope(activityScope, comment);
    }, [activityScope]);

    // Only a cue selection narrows the messages; any other one leaves the key
    // alone, as the tab then shows nothing to match against anyway.
    const commentScope = useMemo(
        () => (activityScope?.supportsComments ? { cueSheetItemId: activityScope.ids } : {}),
        [activityScope]
    );

    // ------------------------------------------------------------------- feeds

    const commentsQuery = useQuery({
        queryKey: [...queryKeys.project(projectId), 'communications', commentScope],
        queryFn: () => CommunicationsService.listCommunicationsAsync(projectId, commentScope),
        enabled: isProjectScope,
    });

    // The cross project feed can carry a long archive, so it is filtered server
    // side; a single project is small enough to filter on the flags themselves.
    // The selection is part of the key: it changes what the server sends back.
    const activitiesKey = useMemo(() => (isProjectScope
        ? [...queryKeys.project(projectId), 'activities', serverScope]
        : ['me', 'activities', showArchived ? 'archived' : 'inbox', serverScope]
    ), [isProjectScope, projectId, showArchived, serverScope]);

    const activitiesQuery = useQuery({
        queryKey: activitiesKey,
        queryFn: () => (isProjectScope
            ? listProjectFeedAsync(projectId, serverScope)
            : CommunicationsService.listMyActivitiesAsync({ archived: showArchived, ...serverScope })),
    });

    const debouncedSearch = useDebounce(commentSearch.trim(), 350);
    const searchTerm = debouncedSearch.length >= SEARCH_MIN_LENGTH ? debouncedSearch : '';

    const searchQuery = useQuery({
        queryKey: [...queryKeys.project(projectId), 'communications', 'search', searchTerm],
        queryFn: () => CommunicationsService.searchCommunicationsAsync(projectId, searchTerm),
        enabled: isProjectScope && Boolean(searchTerm),
    });

    // --------------------------------------------------------------------- mapping

    const lookup = useMemo(() => ({
        cueByCode: buildCueIndex(cueSheetItems),
        cueById: buildCueIdIndex(cueSheetItems),
        userByName: buildUserIndex(team),
        team,
        currentUserId,
        projectsById: (projects || []).reduce((acc, p) => ({ ...acc, [p.id]: p }), {}),
    }), [cueSheetItems, team, currentUserId, projects]);

    const activityItems = useMemo(() => activitiesQuery.data?.items || [], [activitiesQuery.data]);

    /**
     * Drafts still waiting to be confirmed, indexed by the message they came
     * from. They live on the server, so one generated yesterday — or by somebody
     * else — is still there to act on after a reload.
     */
    const drafts = useMemo(() => {
        const index = { ...localDrafts };

        activityItems.forEach(activity => {
            if (activity.status !== 'draft' || !activity.sourceMessageId) return;

            index[activity.sourceMessageId] = toDraft(activity);
        });

        return index;
    }, [activityItems, localDrafts]);

    /**
     * The task each comment ended up producing, once it left the draft stage. It
     * is what lets a comment card say whether anything came of it instead of
     * leaving the reader to guess.
     *
     * Only `assigned_task` counts: a `comment` event also carries the message it
     * belongs to, but it is the notification of the comment itself, not work
     * that was created from it.
     */
    const activityByMessage = useMemo(() => {
        const index = {};

        activityItems.forEach(activity => {
            if (activity.type !== 'assigned_task') return;
            if (!activity.sourceMessageId || activity.status === 'draft') return;

            index[activity.sourceMessageId] = activity;
        });

        return index;
    }, [activityItems]);

    const comments = useMemo(() => {
        const source = searchTerm
            // A semantic hit can be a reply: the feed only shows roots.
            ? (searchQuery.data?.items || []).map(hit => hit.message).filter(m => !m.parentId)
            : (commentsQuery.data?.items || []);

        return source.map(message => {
            const draft = dismissedDrafts.includes(message.id) ? null : drafts[message.id];
            const linked = activityByMessage[message.id];

            return {
                ...toComment(message, lookup),
                draft: draft ? { ...draft, ...(draftEdits[message.id] || {}) } : null,
                linkedActivity: linked ? toLinkedActivity(linked, lookup) : null,
            };
        });
    }, [
        searchTerm, searchQuery.data, commentsQuery.data, lookup,
        drafts, draftEdits, dismissedDrafts, activityByMessage,
    ]);

    const activities = useMemo(
        () => activityItems.map(activity => toActivity(activity, lookup)),
        [activityItems, lookup]
    );

    // ------------------------------------------------------------------ invalidate

    const invalidateComments = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.project(projectId), 'communications'] });
    }, [queryClient, projectId]);

    const invalidateActivities = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.project(projectId), 'activities'] });
        queryClient.invalidateQueries({ queryKey: ['me', 'activities'] });
    }, [queryClient, projectId]);

    /**
     * One place to report a failed action: a 403 means the role changed under
     * us, so the team — and with it the policy — is refetched.
     */
    const reportFailure = useCallback((error, message) => {
        if (handleActionError(error, message)) refreshRole();
    }, [refreshRole]);

    // ------------------------------------------------------------------- mutations

    const createComment = useMutation({
        ...SILENT_ERROR,
        mutationFn: ({ body, cueRefs, cueSheetItemIds, parentId }) => CommunicationsService.createCommunicationAsync(projectId, {
            body,
            ...(cueRefs?.length ? { cue_refs: cueRefs } : {}),
            ...(cueSheetItemIds?.length ? { cue_sheet_item_ids: cueSheetItemIds } : {}),
            ...(parentId ? { parent_id: parentId } : {}),
        }),
    });

    const resolveComment = useMutation({
        ...SILENT_ERROR,
        mutationFn: (messageId) => CommunicationsService.resolveCommunicationAsync(messageId),
        onSuccess: invalidateComments,
    });

    const generateActivity = useMutation({
        ...SILENT_ERROR,
        mutationFn: ({ messageId, name }) => CommunicationsService.generateActivityAsync(messageId, name),
    });

    const editActivity = useMutation({
        ...SILENT_ERROR,
        mutationFn: ({ activityId, body, cueRef, assigneeUserId }) => CommunicationsService.editActivityAsync(
            activityId,
            {
                ...(body !== undefined ? { body } : {}),
                ...(cueRef !== undefined ? { cue_ref: cueRef } : {}),
                ...(assigneeUserId !== undefined ? { assignee_user_id: assigneeUserId } : {}),
            }
        ),
    });

    const confirmActivity = useMutation({
        ...SILENT_ERROR,
        mutationFn: ({ activityId, assigneeUserId }) => CommunicationsService.confirmActivityAsync(
            activityId,
            assigneeUserId
        ),
        onSuccess: () => {
            invalidateActivities();
            invalidateComments();
        },
    });

    const submitReviewMutation = useMutation({
        ...SILENT_ERROR,
        mutationFn: (activityId) => CommunicationsService.submitActivityForReviewAsync(activityId),
        onSuccess: invalidateActivities,
    });

    const approveActivityMutation = useMutation({
        ...SILENT_ERROR,
        mutationFn: (activityId) => CommunicationsService.approveActivityAsync(activityId),
        onSuccess: invalidateActivities,
    });

    // Read and archived are personal flags: no role gates them, and the card has
    // to answer at once, so the cached list is patched before the request lands.
    const patchActivity = useCallback((activityId, patch) => {
        queryClient.setQueryData(activitiesKey, (previous) => (previous?.items
            ? {
                ...previous,
                items: previous.items.map(item => (item.id === activityId ? { ...item, ...patch } : item)),
            }
            : previous));
    }, [activitiesKey, queryClient]);

    const { mutate: setActivityFlag } = useMutation({
        ...SILENT_ERROR,
        mutationFn: ({ activityId, read, archived }) => (read !== undefined
            ? CommunicationsService.setActivityReadAsync(activityId, read)
            : CommunicationsService.setActivityArchivedAsync(activityId, archived)),
        // Marking a card read changes nothing for anybody else, so the optimistic
        // patch is enough; archiving moves it between two server side lists.
        onSettled: (_data, _error, variables) => {
            if (variables?.archived !== undefined) invalidateActivities();
        },
    });

    // ----------------------------------------------------------------------- links

    const projectsById = lookup.projectsById;

    const resolveProject = useCallback((ref, fallbackName) => {
        const project = projectsById[ref];

        return project
            ? { id: project.id, name: project.title || fallbackName }
            : { id: null, name: fallbackName };
    }, [projectsById]);

    const openProject = useCallback((ref, fallbackName) => {
        const project = resolveProject(ref, fallbackName);

        if (!project.id) {
            handleInfoAndDisplay(__('Progetto non disponibile', 'soundbetween'));
            return;
        }

        navigate(`/projects/${project.id}`);
    }, [navigate, resolveProject]);

    const openCue = useCallback((cue) => {
        const target = projectId || cue.projectId;

        if (cueTarget === 'studio' && target) {
            navigate(`/projects/${target}/studio`, { state: { cueCode: cue.code } });
            return;
        }

        const messages = {
            studio: __('Apro la cue %s in My Studio', 'soundbetween'),
            cuesheet: __('Evidenzio la cue %s nella cue sheet', 'soundbetween'),
            timeline: __('Vado alla cue %s sulla timeline', 'soundbetween'),
        };

        handleInfoAndDisplay((messages[cueTarget] || messages.studio).replace('%s', cue.code));
    }, [cueTarget, navigate, projectId]);

    const openMention = useCallback((segment) => {
        handleInfoAndDisplay(
            __('Profilo di %s', 'soundbetween').replace('%s', segment.v.replace('@', ''))
        );
    }, []);

    // ------------------------------------------------------------------ activities

    const markActivityRead = useCallback((id) => {
        patchActivity(id, { read: true });
        setActivityFlag({ activityId: id, read: true });
    }, [patchActivity, setActivityFlag]);

    const markActivityUnread = useCallback((id) => {
        patchActivity(id, { read: false });
        setActivityFlag({ activityId: id, read: false });
    }, [patchActivity, setActivityFlag]);

    const archiveActivity = useCallback((id) => {
        patchActivity(id, { archived: true });
        setActivityFlag({ activityId: id, archived: true });
    }, [patchActivity, setActivityFlag]);

    const unarchiveActivity = useCallback((id) => {
        patchActivity(id, { archived: false });
        setActivityFlag({ activityId: id, archived: false });
    }, [patchActivity, setActivityFlag]);

    const approveActivity = useCallback(async (id) => {
        try {
            await approveActivityMutation.mutateAsync(id);
            handleInfoAndDisplay(__('Attività approvata', 'soundbetween'));
        } catch (error) {
            reportFailure(error, __('Non è stato possibile approvare l’attività', 'soundbetween'));
        }
    }, [approveActivityMutation, reportFailure]);

    /** "Invia in revisione": assigned -> in_review, so it lands in the review list. */
    const submitActivityForReview = useCallback(async (id) => {
        try {
            await submitReviewMutation.mutateAsync(id);
            handleInfoAndDisplay(__('Attività inviata in revisione', 'soundbetween'));
        } catch (error) {
            reportFailure(error, __('Non è stato possibile inviare l’attività in revisione', 'soundbetween'));
        }
    }, [reportFailure, submitReviewMutation]);

    /**
     * The invitation flow lives outside the Comm Center; the API generates no
     * "you were invited" activity yet, so this only clears the card.
     */
    const joinProject = useCallback((id) => markActivityRead(id), [markActivityRead]);

    // ---------------------------------------------------------------- comm actions

    const toggleCommentResolved = useCallback(async (id) => {
        const comment = comments.find(c => c.id === id);

        if (comment?.resolved) {
            handleInfoAndDisplay(__('Un thread risolto non può essere riaperto dall’API', 'soundbetween'));
            return;
        }

        try {
            await resolveComment.mutateAsync(id);
        } catch (error) {
            reportFailure(error, __('Non è stato possibile risolvere il thread', 'soundbetween'));
        }
    }, [comments, reportFailure, resolveComment]);

    /**
     * The cue codes of a message being written, plus the ids they stand for.
     * An homonym is only resolved onto a cue the context points at — see
     * resolveCueIds — and otherwise left for the backend, which leaves it be.
     */
    const cueLinksFor = useCallback((text, preferredIds = []) => {
        const cueRefs = detectCueRefs(text, lookup.cueByCode);
        const selectedIds = selection?.kind === SELECTION_KINDS.CUE ? (selection.ids || []) : [];

        return {
            cueRefs,
            cueSheetItemIds: resolveCueIds(cueRefs, cueSheetItems, [...preferredIds, ...selectedIds]),
        };
    }, [cueSheetItems, lookup.cueByCode, selection]);

    const addReply = useCallback(async (commentId, text) => {
        if (!text.trim()) return;

        // A reply about "1m4" is about the 1m4 its thread is about.
        const parent = comments.find(c => c.id === commentId);

        try {
            await createComment.mutateAsync({
                body: text.trim(),
                ...cueLinksFor(text, parent?.cueSheetItemIds || []),
                parentId: commentId,
            });

            // The thread and the counter both live under the communications key.
            invalidateComments();
        } catch (error) {
            reportFailure(error, __('Non è stato possibile inviare la risposta', 'soundbetween'));
        }
    }, [comments, createComment, cueLinksFor, invalidateComments, reportFailure]);

    // The cue of the activity a comment was started from ("Commenta"), kept
    // until that comment is sent.
    const seedCueIds = useRef([]);

    /**
     * The cues a new comment is about besides the ones its text names: those
     * selected on the page. Writing while "Film intro" is selected is writing
     * about Film intro, whether or not its code is typed — otherwise the
     * comment would not show up when that very cue is selected again.
     */
    const composerCueIds = useMemo(
        () => (activityScope?.supportsComments ? activityScope.ids : []),
        [activityScope]
    );

    const addComment = useCallback(async (text) => {
        if (!text.trim()) return;

        const links = cueLinksFor(text, seedCueIds.current);

        try {
            await createComment.mutateAsync({
                body: text.trim(),
                cueRefs: links.cueRefs,
                cueSheetItemIds: [...new Set([...links.cueSheetItemIds, ...composerCueIds])],
            });
            seedCueIds.current = [];
            invalidateComments();
        } catch (error) {
            reportFailure(error, __('Non è stato possibile pubblicare il commento', 'soundbetween'));
        }
    }, [composerCueIds, createComment, cueLinksFor, invalidateComments, reportFailure]);

    /**
     * "Genera attività": asks the backend for a draft, then opens it for editing.
     * `name` is the optional title the user gave it in the modal.
     */
    const generateDraft = useCallback(async (commentId, name = null) => {
        try {
            const activity = await generateActivity.mutateAsync({ messageId: commentId, name: name?.trim() || null });

            // Shown straight away; the activities refetch takes it over from here.
            setLocalDrafts(prev => ({ ...prev, [commentId]: toDraft(activity) }));
            setDismissedDrafts(prev => prev.filter(id => id !== commentId));
            invalidateActivities();
        } catch (error) {
            reportFailure(error, __('Non è stato possibile generare l’attività', 'soundbetween'));
        }
    }, [generateActivity, invalidateActivities, reportFailure]);

    const updateDraft = useCallback((commentId, patch) => {
        setDraftEdits(prev => ({ ...prev, [commentId]: { ...(prev[commentId] || {}), ...patch } }));
    }, []);

    /**
     * Closes the draft away. The API has no "dismiss" transition, so the
     * activity stays a draft on the server and only this session stops showing it.
     */
    const cancelDraft = useCallback((commentId) => {
        setDismissedDrafts(prev => (prev.includes(commentId) ? prev : [...prev, commentId]));
        setDraftEdits(prev => {
            const next = { ...prev };
            delete next[commentId];

            return next;
        });
        setLocalDrafts(prev => {
            const next = { ...prev };
            delete next[commentId];

            return next;
        });
    }, []);

    /** Confirms the draft: the body is saved, then the activity is assigned. */
    const confirmDraft = useCallback(async (commentId, assigneeUserId) => {
        const draft = comments.find(c => c.id === commentId)?.draft;

        if (!draft?.activityId) return;

        const assignee = assigneeUserId ?? draft.assigneeId ?? null;

        try {
            if (draft.text) {
                await editActivity.mutateAsync({ activityId: draft.activityId, body: draft.text });
            }

            await confirmActivity.mutateAsync({
                activityId: draft.activityId,
                assigneeUserId: assignee,
            });

            cancelDraft(commentId);
            setActiveTab('activities');
            handleInfoAndDisplay(__('Attività creata e assegnata', 'soundbetween'));
        } catch (error) {
            reportFailure(error, __('Non è stato possibile assegnare l’attività', 'soundbetween'));
        }
    }, [comments, confirmActivity, cancelDraft, editActivity, reportFailure]);

    // ------------------------------------------------------------- tab jumping

    useEffect(() => () => clearTimeout(highlightTimer.current), []);

    /** Flags one card, then lets the flag expire on its own. */
    const flash = useCallback((next) => {
        clearTimeout(highlightTimer.current);
        setHighlight(next);
        highlightTimer.current = setTimeout(
            () => setHighlight({ commentId: null, activityId: null }),
            HIGHLIGHT_MS
        );
    }, []);

    /**
     * "Vedi il commento" on an activity card. The filters of the target tab are
     * reset first, otherwise the card would be jumped to and then hidden by a
     * filter that does not match it.
     */
    const openCommentThread = useCallback((messageId) => {
        if (!messageId) return;

        setCommentSearch('');
        setCommentFilter('all');
        flash({ commentId: messageId, activityId: null });
        setActiveTab('communications');
    }, [flash]);

    /** The reverse jump: from the badge on a comment to the activity it created. */
    const openLinkedActivity = useCallback((activityId) => {
        if (!activityId) return;

        setActivityFilter('all');
        setShowArchived(false);
        flash({ commentId: null, activityId });
        setActiveTab('activities');
    }, [flash]);

    /**
     * "Commenta" on a review activity: opens the Communications tab with the
     * composer pre-filled from the activity.
     */
    const [composerSeed, setComposerSeed] = useState('');

    const draftCommentFromActivity = useCallback((activity) => {
        const parts = [
            activity.assigneeName ? `@${activity.assigneeName.split(' ')[0]}` : null,
            activity.cue?.code || null,
        ].filter(Boolean);

        seedCueIds.current = activity.cueSheetItemId ? [activity.cueSheetItemId] : [];
        setComposerSeed(`${parts.join(' ')} `);
        setActiveTab('communications');
    }, []);

    const clearComposerSeed = useCallback(() => setComposerSeed(''), []);

    // ------------------------------------------------------------------- derived

    // A draft is acted on inline, under the comment it came from; the feed shows
    // the tasks, and the informative events the cross project feed adds.
    const feedActivities = useMemo(
        () => activities.filter(a => a.status !== 'draft'),
        [activities]
    );

    const visibleActivities = useMemo(() => {
        const pool = feedActivities.filter(a => Boolean(a.archived) === showArchived && inScope(a));
        const scoped = pool.filter(a => !a.needsReview || showArchived);

        if (activityFilter === 'all') return scoped;

        return scoped.filter(a => a.category === activityFilter);
    }, [feedActivities, activityFilter, showArchived, inScope]);

    const reviewActivities = useMemo(
        () => feedActivities.filter(a => a.needsReview && !a.archived && inScope(a)),
        [feedActivities, inScope]
    );

    // The two tab counters answer for what the tab would actually show, which
    // means they follow the selection: a badge promising ten messages that the
    // scope then hides reads as a bug. The per tab filters are left out — those
    // the user can see and undo; the selection belongs to the whole page.
    const unreadActivityCount = useMemo(
        () => feedActivities.filter(a => a.unread && !a.archived && inScope(a)).length,
        [feedActivities, inScope]
    );

    const visibleComments = useMemo(() => {
        const term = commentSearch.trim().toLowerCase();
        // Beyond SEARCH_MIN_LENGTH the server already answered the query.
        const filterLocally = term && term.length < SEARCH_MIN_LENGTH;

        return comments.filter(c => {
            if (!commentInScope(c)) return false;
            if (commentFilter === 'unresolved' && c.resolved) return false;
            if (commentFilter === 'resolved' && !c.resolved) return false;
            if (commentFilter === 'external' && !c.source) return false;
            if (!filterLocally) return true;

            const haystack = [c.author?.name, c.source, c.rawBody].join(' ').toLowerCase();

            return haystack.includes(term);
        });
    }, [comments, commentFilter, commentSearch, commentInScope]);

    const unresolvedCommentCount = useMemo(
        () => comments.filter(c => !c.resolved && commentInScope(c)).length,
        [comments, commentInScope]
    );

    const value = useMemo(() => ({
        // data
        activities: feedActivities,
        visibleActivities,
        reviewActivities,
        unreadActivityCount,
        comments,
        visibleComments,
        unresolvedCommentCount,
        team,
        hasBrief,
        briefFallback,
        projectId,
        scope,
        isProjectScope,
        // identity & permissions
        currentUserId,
        role,
        can,
        // loading / errors
        loading: commentsQuery.isLoading || activitiesQuery.isLoading || roleLoading,
        searching: searchQuery.isFetching,
        error: commentsQuery.error || activitiesQuery.error || null,
        pending: {
            comment: createComment.isPending,
            resolve: resolveComment.isPending,
            generate: generateActivity.isPending,
            confirm: confirmActivity.isPending || editActivity.isPending,
            review: submitReviewMutation.isPending,
            approve: approveActivityMutation.isPending,
        },
        generatingFor: generateActivity.isPending ? generateActivity.variables?.messageId : null,
        // ui state
        activityScope,
        composerCueLabels: composerCueIds.length ? activityScope.labels : [],
        clearActivityScope,
        activeTab,
        setActiveTab,
        activityFilter,
        setActivityFilter,
        showArchived,
        setShowArchived,
        commentSearch,
        setCommentSearch,
        commentFilter,
        setCommentFilter,
        composerSeed,
        clearComposerSeed,
        highlightCommentId: highlight.commentId,
        highlightActivityId: highlight.activityId,
        // links
        openCommentThread,
        openLinkedActivity,
        resolveProject,
        openProject,
        openCue,
        openMention,
        // activity actions
        markActivityRead,
        markActivityUnread,
        archiveActivity,
        unarchiveActivity,
        approveActivity,
        submitActivityForReview,
        joinProject,
        draftCommentFromActivity,
        // comment actions
        toggleCommentResolved,
        addReply,
        addComment,
        generateDraft,
        updateDraft,
        cancelDraft,
        confirmDraft,
    }), [
        hasBrief, briefFallback, projectId, scope, isProjectScope,
        feedActivities, visibleActivities, reviewActivities, unreadActivityCount,
        comments, visibleComments, unresolvedCommentCount, team,
        currentUserId, role, can, roleLoading,
        commentsQuery.isLoading, commentsQuery.error,
        activitiesQuery.isLoading, activitiesQuery.error,
        searchQuery.isFetching,
        createComment.isPending, resolveComment.isPending, editActivity.isPending,
        generateActivity.isPending, generateActivity.variables,
        confirmActivity.isPending, submitReviewMutation.isPending,
        approveActivityMutation.isPending,
        activityScope, clearActivityScope, composerCueIds,
        activeTab, activityFilter, showArchived, commentSearch, commentFilter,
        composerSeed, clearComposerSeed, highlight,
        openCommentThread, openLinkedActivity,
        resolveProject, openProject, openCue, openMention,
        markActivityRead, markActivityUnread, archiveActivity, unarchiveActivity, approveActivity,
        submitActivityForReview, joinProject, draftCommentFromActivity,
        toggleCommentResolved, addReply, addComment,
        generateDraft, updateDraft, cancelDraft, confirmDraft,
    ]);

    return (
        <CommCenterContext.Provider value={value}>
            {children}
        </CommCenterContext.Provider>
    )
}

export default CommCenterContext;
